/**
 * The system's view of a library entry, for CaseBlink staff: where the agents
 * read it, whether they edit or reproduce it, the layout they reproduce, the
 * text they read, the suggestion's model and transcript, the converter's own
 * numbers, and a Re-render. Folded until asked for; nothing loads until then.
 */
import * as Sentry from "@sentry/react";
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";

import type { FirmLibraryEntry, LayoutProfile } from "../types";
import { TechnicalDetails, type ConversionControls } from "./ConversionPanel";
import { LayoutPanel } from "./LayoutPanel";

type Props = {
  entry: FirmLibraryEntry;
  scope: FirmScope;
  agentPath?: string;
  conversion: ConversionControls;
  busy: boolean;
  onRerender: () => void;
};

const prettyTranscript = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const Row: FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <section className="space-y-1.5 py-3 first:pt-0 last:pb-0">
    <h4 className="text-sm font-medium text-fg-2">{label}</h4>
    {children}
  </section>
);

/** One lazily fetched text, shown in a scrolling block. */
const LazyText: FC<{
  label: string;
  load: () => Promise<string>;
}> = ({ label, load }) => {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  return text === null ? (
    <Button
      variant="secondary"
      size="xs"
      disabled={loading}
      onClick={() => {
        setLoading(true);
        load()
          .then(setText)
          .catch((error) => {
            Sentry.captureException(error);
            setText(`Could not load the ${label.toLowerCase()}.`);
          })
          .finally(() => setLoading(false));
      }}
    >
      {loading ? <Loader2 className="animate-spin" /> : null}
      Show {label}
    </Button>
  ) : (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-fill-1 p-3 font-mono text-sm text-fg-2">
      {text}
    </pre>
  );
};

const Body: FC<Props> = ({
  entry,
  scope,
  agentPath,
  conversion,
  busy,
  onRerender
}) => {
  const [layout, setLayout] = useState<LayoutProfile | null>(null);
  useEffect(() => {
    if (!entry.has_layout) return undefined;
    let cancelled = false;
    firmKnowledgeApi
      .getEntryLayout(entry.id, scope)
      .then((data) => {
        if (!cancelled) setLayout(data);
      })
      .catch((error) => Sentry.captureException(error));
    return () => {
      cancelled = true;
    };
  }, [entry.id, entry.has_layout, scope]);

  const suggestion = entry.suggested;
  const suggestionMeta = suggestion
    ? [
        suggestion.status,
        suggestion.model,
        suggestion.producer_version,
        suggestion.generated_at
          ? new Date(suggestion.generated_at).toLocaleString()
          : null
      ].filter(Boolean)
    : [];

  return (
    <div className="divide-y divide-line-1 pt-3">
      <Row label="How the Agents Read It">
        {agentPath ? (
          <p
            className="truncate font-mono text-sm text-fg-2"
            title={`Every agent at the firm reads it at ${agentPath}`}
            data-testid="entry-agent-path"
          >
            {agentPath}
          </p>
        ) : null}
        <p className="text-sm text-fg-3" data-testid="entry-agent-mode">
          {entry.authorable
            ? "Agents copy and edit this file"
            : "Agents reproduce this file"}
        </p>
      </Row>

      {entry.has_layout ? (
        <Row label="Layout">
          {layout ? (
            <LayoutPanel layout={layout} />
          ) : (
            <div className="flex items-center gap-2 text-sm text-fg-3">
              <Loader2 className="size-3 animate-spin" /> Reading layout…
            </div>
          )}
        </Row>
      ) : null}

      {entry.has_rendering ? (
        <Row
          label={`Text the Agents Read${
            entry.rendering_source
              ? ` (${entry.rendering_source.replace(/_/g, " ")})`
              : ""
          }`}
        >
          <LazyText
            label="Text"
            load={() => firmKnowledgeApi.getEntryRendering(entry.id, scope)}
          />
        </Row>
      ) : null}

      {suggestion ? (
        <Row label="Suggestion">
          <p className="text-sm text-fg-3">{suggestionMeta.join(" · ")}</p>
          {suggestion.status === "failed" && suggestion.error ? (
            <p className="text-sm text-fg-3">{suggestion.error}</p>
          ) : null}
          {suggestion.has_transcript ? (
            <LazyText
              label="Transcript"
              load={() =>
                firmKnowledgeApi
                  .getSuggestionTranscript(entry.id, scope)
                  .then(prettyTranscript)
              }
            />
          ) : null}
        </Row>
      ) : null}

      {entry.is_pdf || entry.conversion ? (
        <Row label="Word Copy">
          <TechnicalDetails conversion={conversion} />
        </Row>
      ) : null}

      <Row label="Rendering">
        <p className="text-sm text-fg-3">
          {entry.rendering_status}
          {entry.rendering_error ? ` · ${entry.rendering_error}` : ""}
        </p>
        <Button
          variant="secondary"
          size="xs"
          disabled={busy}
          onClick={onRerender}
          data-testid="entry-rerender"
        >
          <RefreshCw /> Re-render
        </Button>
      </Row>
    </div>
  );
};

export const EntryTechnicalDetails: FC<Props> = (props) => {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-testid="entry-technical"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm font-medium text-fg-3 hover:text-fg-2"
          data-testid="entry-technical-toggle"
        >
          <Chevron className="size-3.5" />
          Technical Details
          <span className="font-normal text-fg-4">· CaseBlink staff only</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Body {...props} />
      </CollapsibleContent>
    </Collapsible>
  );
};
